import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { MapParams } from '../../@types/map';
import L from 'leaflet';
import { Line, LineStop } from '../../@types/lines';
import LineColor from './LineColor';
import { decode } from '@googlemaps/polyline-codec';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import ZoomInMapIcon from '@mui/icons-material/ZoomInMap';
import { Fab } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useRequirementStopSelectionContext } from '../../hooks/useRequirementStopSelectionContext';
import { useRequirementStopPassengerDataContext } from '../../hooks/useRequirementStopPassengerCount';
import DayCheckboxControl from './DayCheckboxControl';
import LineStopMarkers, { StopWithLine } from './LineStopMarkers';
import { FactoryIcon, MakeColorIcon } from '../Layout/styles';

L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

type FlyMapToProps = {
    lat: number;
    lon: number;
    mapExpanded: boolean;
};

const FlyMapTo = ({ lat, lon, mapExpanded }: FlyMapToProps) => {
    const map = useMap();

    useEffect(() => {
        map.invalidateSize();
        map.flyTo([lat, lon]);
    }, [lat, lon, mapExpanded]);

    return null;
};

const PlannerMap = ({
    requirements,
    selectedRequirementID,
    onSelectRequirement,
    stops,
    lines,
    allLines,
    factory,
    routes,
    mapExpanded,
    setMapExpanded,
    showRoutes,
    setShowRoutes,
}: MapParams) => {
    const { stopSelectionStatus, setStopSelectionStatus } = useRequirementStopSelectionContext();
    const { requirementStopPassengerData, calculatePassengers } = useRequirementStopPassengerDataContext();
    const [lineStops, setLineStops] = useState<StopWithLine[]>([]);

    const stopPassengers = useMemo(() => {
        return calculatePassengers(selectedRequirementID);
    }, [requirementStopPassengerData, selectedRequirementID]);

    useEffect(() => {
        setLineStops(
            lines
                .flatMap((line: Line) => {
                    return line.Stops.map(s => {
                        return {
                            ...s,
                            LineName: line.Name,
                            LineID: line.ID,
                        };
                    });
                })
                .filter((value, index, self) => self.map(x => x.Name).indexOf(value.Name) === index)
        );
    }, [lines]);

    const siteStops: LineStop[] = lines
        .flatMap((line: Line) => line.Stops.filter(s => s.Type === 'site'))
        .filter((value, index, self) => self.map(x => x.Name).indexOf(value.Name) === index);

    return (
        <MapContainer center={[factory.lat, factory.lon]} zoom={10} scrollWheelZoom={true} style={{ height: '100%', width: '100%' }}>
            <Fab
                size={'small'}
                sx={{ position: 'absolute', right: '10px', top: '10px' }}
                onClick={() => {
                    setMapExpanded(!mapExpanded);
                }}>
                {mapExpanded ? <ZoomInMapIcon fontSize={'medium'} /> : <ZoomOutMapIcon fontSize={'medium'} />}
            </Fab>
            <FlyMapTo lat={factory.lat} lon={factory.lon} mapExpanded={mapExpanded} />

            <Fab size={'small'} sx={{ position: 'absolute', right: '10px', top: '60px' }} onClick={() => setShowRoutes(!showRoutes)}>
                {showRoutes ? <VisibilityOffIcon fontSize={'medium'} /> : <VisibilityIcon fontSize={'medium'} />}
            </Fab>

            <DayCheckboxControl requirements={requirements} selectedRequirementID={selectedRequirementID} onChange={onSelectRequirement} />

            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
            />

            {siteStops.map(s => (
                <Marker icon={FactoryIcon('red')} key={`${s.ID}.${s.Lat}_${s.Lon}`} position={[s.Lat, s.Lon]}>
                    <Popup>
                        {factory.name}, {s.Name}
                    </Popup>
                </Marker>
            ))}

            {stops.map(s => {
                const stopPassenger = stopPassengers.find(sp => sp.CrmID === s.crmId);
                const passenger = stopPassenger ? stopPassenger.NPassengers : 0;
                const color = passenger > 0 ? 'black' : 'grey';
                return (
                    <Marker
                        icon={MakeColorIcon(color, stopSelectionStatus[s.id] ? 'red' : color, passenger)}
                        key={`${s.id}.${s.lat}_${s.lon}`}
                        position={[s.lat, s.lon]}
                        eventHandlers={{
                            click: () => {
                                const newStopCheckbox = { ...stopSelectionStatus, [s.id]: !stopSelectionStatus[s.id] };
                                setStopSelectionStatus(newStopCheckbox);
                            },
                        }}>
                        <Popup>{s.name}</Popup>
                    </Marker>
                );
            })}
            <LineStopMarkers lineStops={lineStops.filter(stop => stop.Type === 'stop')} stopPassengers={stopPassengers} allLines={allLines} />
            {routes.Routes.filter(r => lines.map(ln => ln.ID).indexOf(r.LineID) > -1).map((route, idx) => {
                const points = decode(route.Route, 6);
                return <Polyline key={idx + '_' + route.LineID} positions={points} color={LineColor(allLines, route.LineID)} weight={5} />;
            })}
        </MapContainer>
    );
};

export { PlannerMap };
